// ./src/hooks/useAuth.ts
import { useState, useCallback, useEffect } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  AuthService,
  SignInParams,
  SignUpParams,
  ResetPasswordRequestParams,
  ResetPasswordParams,
  User,
} from "@/utils/api";

export interface UseAuthOptions {
  /** Auto-check authentication on mount */
  autoCheck?: boolean;
}

export interface UseAuthReturn {
  // Data state
  user: User | null;
  isAuthenticated: boolean;

  // Loading states
  loading: boolean;
  isSigningIn: boolean;
  isSigningUp: boolean;
  isRefreshing: boolean;
  isSigningOut: boolean;
  isRequestingReset: boolean;
  isResettingPassword: boolean;

  // Core operations (matching API method names exactly)
  me: () => Promise<User | null>;
  signIn: (params: SignInParams) => Promise<User | null>;
  signUp: (params: SignUpParams) => Promise<User | null>;
  refreshToken: () => Promise<User | null>;
  signOut: () => Promise<boolean>;
  resetPasswordRequest: (
    params: ResetPasswordRequestParams
  ) => Promise<boolean>;
  resetPassword: (params: ResetPasswordParams) => Promise<boolean>;

  // Helper operations
  reset: () => void;
  checkAuth: () => Promise<void>;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

export const useAuth = (options: UseAuthOptions = {}): UseAuthReturn => {
  const { autoCheck = true } = options;

  // Data state
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isRequestingReset, setIsRequestingReset] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setUser(null);
    setIsAuthenticated(false);
    setLastError(null);
    setLoading(false);
    setIsSigningIn(false);
    setIsSigningUp(false);
    setIsRefreshing(false);
    setIsSigningOut(false);
    setIsRequestingReset(false);
    setIsResettingPassword(false);
  }, []);

  // Helper to update user state
  const updateUserState = useCallback((userData: User | null) => {
    if (userData) {
      setUser(userData);
      setIsAuthenticated(true);
      AuthService.storeUserData(userData);
    } else {
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  // Get current user (matching API method name)
  const me = useCallback(async (): Promise<User | null> => {
    setLoading(true);
    setLastError(null);

    try {
      const result = await AuthService.me();

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const userData = result.data[0] as User;
        updateUserState(userData);
        setLastError(null);
        return userData;
      } else {
        const errorMessage =
          result.errors?.[0]?.errors?.[0] || "Failed to get user information";
        setLastError(errorMessage);
        updateUserState(null);
        return null;
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to get user information";
      setLastError(errorMessage);
      updateUserState(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [updateUserState]);

  // Sign in user (matching API method name)
  const signIn = useCallback(
    async (params: SignInParams): Promise<User | null> => {
      setIsSigningIn(true);
      setLastError(null);

      try {
        const result = await AuthService.signIn(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const userData = result.data[0] as User;
          updateUserState(userData);
          setLastError(null);
          return userData;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to sign in";
          setLastError(errorMessage);
          updateUserState(null);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to sign in";
        setLastError(errorMessage);
        updateUserState(null);
        return null;
      } finally {
        setIsSigningIn(false);
      }
    },
    [updateUserState]
  );

  // Sign up user (matching API method name)
  const signUp = useCallback(
    async (params: SignUpParams): Promise<User | null> => {
      setIsSigningUp(true);
      setLastError(null);

      try {
        const result = await AuthService.signUp(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const userData = result.data[0] as User;
          updateUserState(userData);
          setLastError(null);
          return userData;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to sign up";
          setLastError(errorMessage);
          updateUserState(null);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to sign up";
        setLastError(errorMessage);
        updateUserState(null);
        return null;
      } finally {
        setIsSigningUp(false);
      }
    },
    [updateUserState]
  );

  // Refresh token (matching API method name)
  const refreshToken = useCallback(async (): Promise<User | null> => {
    setIsRefreshing(true);
    setLastError(null);

    try {
      const result = await AuthService.refreshToken();

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const userData = result.data[0] as User;
        updateUserState(userData);
        setLastError(null);
        return userData;
      } else {
        const errorMessage =
          result.errors?.[0]?.errors?.[0] || "Failed to refresh token";
        setLastError(errorMessage);
        updateUserState(null);
        return null;
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to refresh token";
      setLastError(errorMessage);
      updateUserState(null);
      return null;
    } finally {
      setIsRefreshing(false);
    }
  }, [updateUserState]);

  // Sign out user (matching API method name)
  const signOut = useCallback(async (): Promise<boolean> => {
    setIsSigningOut(true);
    setLastError(null);

    try {
      const result = await AuthService.signOut();

      // Always clear local state regardless of server response
      updateUserState(null);

      if (result.code === OP_RESULT_CODES.OK) {
        setLastError(null);
        return true;
      } else {
        const errorMessage =
          result.errors?.[0]?.errors?.[0] ||
          "Sign out completed locally, but server error occurred";
        setLastError(errorMessage);
        // Still return true since local cleanup was successful
        return true;
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Sign out completed locally, but server error occurred";
      setLastError(errorMessage);
      updateUserState(null);
      // Still return true since local cleanup was successful
      return true;
    } finally {
      setIsSigningOut(false);
    }
  }, [updateUserState]);

  // Request password reset (matching API method name)
  const resetPasswordRequest = useCallback(
    async (params: ResetPasswordRequestParams): Promise<boolean> => {
      setIsRequestingReset(true);
      setLastError(null);

      try {
        const result = await AuthService.resetPasswordRequest(params);

        if (result.code === OP_RESULT_CODES.OK) {
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to request password reset";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to request password reset";
        setLastError(errorMessage);
        return false;
      } finally {
        setIsRequestingReset(false);
      }
    },
    []
  );

  // Reset password (matching API method name)
  const resetPassword = useCallback(
    async (params: ResetPasswordParams): Promise<boolean> => {
      setIsResettingPassword(true);
      setLastError(null);

      try {
        const result = await AuthService.resetPassword(params);

        if (result.code === OP_RESULT_CODES.OK) {
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to reset password";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to reset password";
        setLastError(errorMessage);
        return false;
      } finally {
        setIsResettingPassword(false);
      }
    },
    []
  );

  // Check authentication status
  const checkAuth = useCallback(async () => {
    // First check if we have stored user data
    const storedUser = AuthService.getStoredUserData();
    if (storedUser) {
      setUser(storedUser);
      setIsAuthenticated(true);
    }

    // Then verify with server
    await me();
  }, [me]);

  // Auto-check authentication on mount
  const [hasAutoChecked, setHasAutoChecked] = useState(false);

  useEffect(() => {
    if (autoCheck && !hasAutoChecked) {
      setHasAutoChecked(true);
      checkAuth();
    }
  }, [autoCheck, hasAutoChecked, checkAuth]);

  return {
    user,
    isAuthenticated,
    loading,
    isSigningIn,
    isSigningUp,
    isRefreshing,
    isSigningOut,
    isRequestingReset,
    isResettingPassword,
    me,
    signIn,
    signUp,
    refreshToken,
    signOut,
    resetPasswordRequest,
    resetPassword,
    reset,
    checkAuth,
    lastError,
    clearError,
  };
};
